import { ValidationRule, ValidationResult } from './types';
import { StringUtils } from '../utils/stringUtils';
import { PhoneUtils } from '../utils/phoneUtils';

/**
 * Collection of reusable validation rules
 * Each rule is a pure function that returns a ValidationResult
 */
export class ValidationRules {
  /**
   * Validate minimum string length
   */
  static minLength(min: number, message?: string): ValidationRule<string> {
    return {
      name: 'minLength',
      validate: (value: string): ValidationResult => {
        const isValid = value.length >= min;
        return {
          isValid,
          errors: isValid ? [] : [message || `Must be at least ${min} characters long`]
        };
      }
    };
  }

  /**
   * Validate maximum string length
   */
  static maxLength(max: number, message?: string): ValidationRule<string> {
    return {
      name: 'maxLength',
      validate: (value: string): ValidationResult => {
        const isValid = value.length <= max;
        return {
          isValid,
          errors: isValid ? [] : [message || `Must be no more than ${max} characters long`]
        };
      }
    };
  }

  /**
   * Validate email format using existing StringUtils
   */
  static email(message?: string): ValidationRule<string> {
    return {
      name: 'email',
      validate: (value: string): ValidationResult => {
        const isValid = StringUtils.isValidEmail(value);
        return {
          isValid,
          errors: isValid ? [] : [message || 'Please enter a valid email address']
        };
      }
    };
  }

  /**
   * Validate phone number using existing PhoneUtils
   */
  static phone(country: string = 'US', message?: string): ValidationRule<string> {
    return {
      name: 'phone',
      validate: (value: string): ValidationResult => {
        const isValid = PhoneUtils.validate(value, country);
        return {
          isValid,
          errors: isValid ? [] : [message || 'Please enter a valid phone number']
        };
      }
    };
  }

  /**
   * Validate against a regular expression pattern
   */
  static pattern(regex: RegExp, message?: string): ValidationRule<string> {
    return {
      name: 'pattern',
      validate: (value: string): ValidationResult => {
        const isValid = regex.test(value);
        return {
          isValid,
          errors: isValid ? [] : [message || 'Invalid format']
        };
      }
    };
  }

  /**
   * Validate minimum numeric value
   */
  static min(min: number, message?: string): ValidationRule<number> {
    return {
      name: 'min',
      validate: (value: number): ValidationResult => {
        const isValid = value >= min;
        return {
          isValid,
          errors: isValid ? [] : [message || `Must be at least ${min}`]
        };
      }
    };
  }

  /**
   * Validate maximum numeric value
   */
  static max(max: number, message?: string): ValidationRule<number> {
    return {
      name: 'max',
      validate: (value: number): ValidationResult => {
        const isValid = value <= max;
        return {
          isValid,
          errors: isValid ? [] : [message || `Must be no more than ${max}`]
        };
      }
    };
  }

  /**
   * Validate that value is one of allowed options
   */
  static oneOf<T>(options: T[], message?: string): ValidationRule<T> {
    return {
      name: 'oneOf',
      validate: (value: T): ValidationResult => {
        const isValid = options.includes(value);
        return {
          isValid,
          errors: isValid ? [] : [message || `Must be one of: ${options.join(', ')}`]
        };
      }
    };
  }

  /**
   * Validate credit card number using Luhn algorithm
   */
  static creditCard(message?: string): ValidationRule<string> {
    return {
      name: 'creditCard',
      validate: (value: string): ValidationResult => {
        const cleanNumber = value.replaceAll(/\D/g, '');
        if (cleanNumber.length !== 16) {
          return {
            isValid: false,
            errors: ['Credit card number must be 16 digits']
          };
        }

        // Luhn algorithm
        let sum = 0;
        let isEven = false;

        for (let i = cleanNumber.length - 1; i >= 0; i--) {
          let digit = Number.parseInt(cleanNumber[i]);

          if (isEven) {
            digit *= 2;
            if (digit > 9) {
              digit -= 9;
            }
          }

          sum += digit;
          isEven = !isEven;
        }

        const isValid = sum % 10 === 0;
        return {
          isValid,
          errors: isValid ? [] : [message || 'Please enter a valid credit card number']
        };
      }
    };
  }

  /**
   * Validate credit card expiration date (MM/YY format)
   */
  static creditCardExpiration(_message?: string): ValidationRule<string> {
    return {
      name: 'creditCardExpiration',
      validate: (value: string): ValidationResult => {
        if (!/^\d{2}\/\d{2}$/.test(value)) {
          return {
            isValid: false,
            errors: ['Expiration date must be in MM/YY format']
          };
        }

        const [month, year] = value.split('/').map(Number);
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear() % 100;
        const currentMonth = currentDate.getMonth() + 1;

        if (month < 1 || month > 12) {
          return {
            isValid: false,
            errors: ['Invalid month']
          };
        }

        if (year < currentYear || (year === currentYear && month < currentMonth)) {
          return {
            isValid: false,
            errors: ['Card has expired']
          };
        }

        return { isValid: true, errors: [] };
      }
    };
  }

  /**
   * Validate Postal code format
   */
  static postalCode(message?: string): ValidationRule<string> {
    return {
      name: 'postalCode',
      validate: (value: string): ValidationResult => {
        const cleanPostalCode = value.replaceAll(/\D/g, '');
        const isValid = cleanPostalCode.length === 5;
        return {
          isValid,
          errors: isValid ? [] : [message || 'Postal code must be 5 digits']
        };
      }
    };
  }

  /**
   * Validate service area Postal code (accepts array of valid Postal codes)
   */
  static serviceAreaPostalCode(validPostalCodes: string[], message?: string): ValidationRule<string> {
    return {
      name: 'serviceAreaPostalCode',
      validate: (value: string): ValidationResult => {
        const cleanPostalCode = value.replaceAll(/\D/g, '');
        if (cleanPostalCode.length !== 5) {
          return {
            isValid: false,
            errors: ['Postal code must be 5 digits']
          };
        }

        const isValid = validPostalCodes.includes(cleanPostalCode);
        return {
          isValid,
          errors: isValid ? [] : [message || 'Sorry, we don\'t currently service this area']
        };
      }
    };
  }

  /**
   * Custom validation function
   */
  static custom<T>(validator: (value: T, context?: Record<string, unknown>) => boolean, message: string): ValidationRule<T> {
    return {
      name: 'custom',
      validate: (value: T, context?: Record<string, unknown>): ValidationResult => {
        const isValid = validator(value, context);
        return {
          isValid,
          errors: isValid ? [] : [message]
        };
      }
    };
  }

  /**
   * Validate date format (YYYY-MM-DD)
   */
  static dateFormat(message?: string): ValidationRule<string> {
    return {
      name: 'dateFormat',
      validate: (value: string): ValidationResult => {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(value)) {
          return {
            isValid: false,
            errors: [message || 'Date must be in YYYY-MM-DD format']
          };
        }
        
        // Validate it's a real date
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const isValid = !isNaN(date.getTime()) && 
                       date.getFullYear() === year &&
                       date.getMonth() === month - 1 &&
                       date.getDate() === day;
        
        return {
          isValid,
          errors: isValid ? [] : [message || 'Invalid date']
        };
      }
    };
  }

  /**
   * Validate time format (HH:MM in 24-hour format)
   */
  static timeFormat(message?: string): ValidationRule<string> {
    return {
      name: 'timeFormat',
      validate: (value: string): ValidationResult => {
        const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
        const isValid = timeRegex.test(value);
        return {
          isValid,
          errors: isValid ? [] : [message || 'Time must be in HH:MM format (24-hour)']
        };
      }
    };
  }

  /**
   * Validate that a date is in the future
   */
  static futureDate(message?: string): ValidationRule<string> {
    return {
      name: 'futureDate',
      validate: (value: string): ValidationResult => {
        const [year, month, day] = value.split('-').map(Number);
        const inputDate = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const isValid = inputDate >= today;
        return {
          isValid,
          errors: isValid ? [] : [message || 'Date must be in the future']
        };
      }
    };
  }

  /**
   * Validate minimum notice requirement (date/time must be X hours in the future)
   */
  static minimumNotice(hours: number, message?: string): ValidationRule<{ date: string; time: string }> {
    return {
      name: 'minimumNotice',
      validate: (value: { date: string; time: string }): ValidationResult => {
        const { date, time } = value;
        if (!date || !time) {
          return {
            isValid: false,
            errors: ['Date and time are required']
          };
        }

        const [year, month, day] = date.split('-').map(Number);
        const [hour, minute] = time.split(':').map(Number);
        const scheduledDateTime = new Date(year, month - 1, day, hour, minute);
        const now = new Date();
        const minimumDateTime = new Date(now.getTime() + hours * 60 * 60 * 1000);

        const isValid = scheduledDateTime >= minimumDateTime;
        return {
          isValid,
          errors: isValid ? [] : [message || `Service must be scheduled at least ${hours} hours in advance`]
        };
      }
    };
  }
}